import { createContext, useContext, useState, type ReactNode } from "react";
import type {
  Task,
  LookupValue,
  TemplateTask,
  CreateTaskForm,
} from "@/types/task";

// Import React Query hooks
import {
  useUserTasks,
  useTemplates,
  useLookupValues,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
} from "@/hooks/useTasks";

// Import real-time subscription hook
import { useTasksRealtime, useStepsRealtime } from "@/hooks/useTasksRealtime";

interface TaskContextProps {
  // Tasks
  tasks: Task[];
  selectedTask: Task | null;
  loading: boolean;
  error: string | null;
  isUpdating: boolean; // Loading state for task updates (e.g., drag-and-drop)

  // Lookup data
  categories: LookupValue[];
  statuses: LookupValue[];
  priorities: LookupValue[];
  templates: TemplateTask[];

  // Actions
  loadTasks: () => Promise<void>;
  selectTask: (task: Task | null) => void;
  createTask: (taskData: CreateTaskForm) => Promise<Task>;
  updateTask: (taskId: string, updates: Partial<Task>) => Promise<Task>;
  deleteTask: (taskId: string) => Promise<void>;
  refreshTask: (taskId: string) => Promise<void>;
  getTemplatesByLayer: (layerName: string) => TemplateTask[];
}

const TaskContext = createContext<TaskContextProps | undefined>(undefined);

export function TaskProvider({ children }: { children: ReactNode }) {
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Enable real-time subscriptions for tasks
  useTasksRealtime();

  // Enable real-time subscriptions for all steps (global, not filtered by task)
  useStepsRealtime();

  // React Query hooks for data fetching
  const {
    data: tasks = [],
    isLoading: tasksLoading,
    error: tasksError,
    refetch: refetchTasks,
  } = useUserTasks();
  const {
    data: categories = [],
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useLookupValues("category");
  const {
    data: statuses = [],
    isLoading: statusesLoading,
    error: statusesError,
  } = useLookupValues("task_status");
  const {
    data: priorities = [],
    isLoading: prioritiesLoading,
    error: prioritiesError,
  } = useLookupValues("priority");
  const {
    data: templates = [],
    isLoading: templatesLoading,
    error: templatesError,
  } = useTemplates();

  // React Query mutations
  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const deleteTaskMutation = useDeleteTask();

  // Compute loading and error states
  const loading =
    tasksLoading ||
    categoriesLoading ||
    statusesLoading ||
    prioritiesLoading ||
    templatesLoading;
  const error =
    tasksError?.message ||
    categoriesError?.message ||
    statusesError?.message ||
    prioritiesError?.message ||
    templatesError?.message ||
    createTaskMutation.error?.message ||
    updateTaskMutation.error?.message ||
    deleteTaskMutation.error?.message ||
    null;

  // Actions
  const loadTasks = async (): Promise<void> => {
    await refetchTasks();
  };

  const selectTask = (task: Task | null) => {
    setSelectedTask(task);
  };

  const createTask = async (taskData: CreateTaskForm): Promise<Task> => {
    return new Promise((resolve, reject) => {
      createTaskMutation.mutate(taskData, {
        onSuccess: (newTask) => {
          resolve(newTask);
        },
        onError: (error) => {
          reject(error);
        },
      });
    });
  };

  const updateTask = async (
    taskId: string,
    updates: Partial<Task>
  ): Promise<Task> => {
    return new Promise((resolve, reject) => {
      updateTaskMutation.mutate(
        { taskId, taskData: updates },
        {
          onSuccess: (updatedTask) => {
            if (updatedTask) {
              // Update selected task if it's the one being updated
              if (selectedTask?.id === taskId) {
                setSelectedTask(updatedTask);
              }
              resolve(updatedTask);
            } else {
              reject(new Error("Failed to update task"));
            }
          },
          onError: (error) => {
            reject(error);
          },
        }
      );
    });
  };

  const deleteTask = async (taskId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      deleteTaskMutation.mutate(taskId, {
        onSuccess: () => {
          // Clear selected task if it was deleted
          if (selectedTask?.id === taskId) {
            setSelectedTask(null);
          }
          resolve();
        },
        onError: (error) => {
          reject(error);
        },
      });
    });
  };

  const refreshTask = async (taskId: string): Promise<void> => {
    console.log("Refreshing task:", taskId);
    // For now, we'll just refetch all tasks
    // TODO: Could be optimized to only refetch the specific task
    await refetchTasks();
  };

  const getTemplatesByLayer = (layerName: string): TemplateTask[] => {
    if (!layerName || layerName === "no_layer") {
      return templates;
    }
    return templates.filter(
      (template) =>
        template.layer_name === layerName || template.layer_name === "no_layer"
    );
  };

  const value = {
    tasks,
    selectedTask,
    loading,
    error,
    isUpdating: updateTaskMutation.isPending,
    categories,
    statuses,
    priorities,
    templates,
    loadTasks,
    selectTask,
    createTask,
    updateTask,
    deleteTask,
    refreshTask,
    getTemplatesByLayer,
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
}

export function useTaskContext() {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error("useTaskContext must be used within a TaskProvider");
  }
  return context;
}
